import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Users as UsersIcon,
  Star,
  MessageCircle,
  ThumbsUp,
  CalendarDays,
  ArrowUpRight,
  Download,
} from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshButton } from "@/components/RefreshButton";
import { useSeo } from "@/lib/useSeo";
import { boursoramaUserUrl, fromNowFR, formatNumber } from "@/lib/utils";

type UserDetail = {
  id: number;
  username: string;
  slug: string | null;
  member_since: string | null;
  posts_count: number;
  boursostars_stars: number | null;
  posts_created_count: number | null;
  posts_liked_count: number | null;
  likes_received_count: number | null;
  following_count: number | null;
  followers_count: number | null;
  recent: Array<{
    id: number;
    thread_id: number;
    content: string;
    posted_at: string;
    thread_title: string | null;
    thread_url: string | null;
    forum_slug: string | null;
    forum_title: string | null;
  }>;
};

export default function UserDetailPage({ id: idProp }: { id?: string } = {}) {
  // The router mounts this under /users/:segment, so the param is `segment`,
  // not `id`. Accept an explicit prop (passed by UsersRouter) and fall back to
  // either param name so the page works however it's mounted.
  const params = useParams();
  const id = idProp ?? params.id ?? params.segment;
  const { data, isLoading } = useQuery<UserDetail>({
    queryKey: ["user", id],
    queryFn: () => api.user(id!),
    enabled: !!id,
  });

  // noindex: this page surfaces excerpts of the user's scraped posts (raw
  // Boursorama content) and is thin per-page. Keeping it out of the index
  // avoids both the duplicate-content and the thin-content signals — the
  // aggregated /classements and /stats pages carry the original SEO value.
  useSeo({
    title: data?.username ? `${data.username} — profil` : "Profil utilisateur",
    canonicalPath: `/users/${id}`,
    noindex: true,
  });

  const { data: history = [] } = useQuery({
    queryKey: ["user-history", id],
    queryFn: () => api.userHistory(id!),
    enabled: !!id,
  });

  const { data: graph } = useQuery({
    queryKey: ["user-graph", id],
    queryFn: () => api.userFollowGraph(id!),
    enabled: !!id,
  });

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              <CardTitle className="text-2xl truncate" title={data.username}>{data.username}</CardTitle>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-muted">
                {data.member_since && (
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="size-4" />
                    Membre depuis {fromNowFR(data.member_since)}
                  </span>
                )}
                {data.slug && data.slug !== data.username && (
                  <Badge variant="secondary">id: {data.slug}</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0">
              <RefreshButton
                kind="user"
                id={data.id}
                label="Importer le profil complet"
                invalidate={[["user", String(data.id)], ["user-history", String(data.id)], ["user-graph", String(data.id)]]}
              />
              <a
                href={boursoramaUserUrl(data.slug ?? data.username)}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                Profil Boursorama <ArrowUpRight className="size-3.5" />
              </a>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats grid. `posts_count` (messages observés par notre scraper) is
          always populated ; the four profile metrics below only exist once the
          full Boursorama profile has been imported. When they're all null we
          show a single CTA strip instead of a row of dead "—". */}
      {(() => {
        const profileImported =
          data.posts_created_count != null ||
          data.posts_liked_count != null ||
          data.likes_received_count != null ||
          data.following_count != null ||
          data.followers_count != null;
        return (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard
              icon={<MessageCircle className="size-4" />}
              label="Messages observés"
              value={data.posts_count}
              hint="Messages de ce membre captés par notre scraper sur les fils suivis."
            />
            {profileImported ? (
              <>
                <StatCard
                  icon={<MessageCircle className="size-4" />}
                  label="Posts créés"
                  value={data.posts_created_count}
                  hint="Total de messages publiés, d'après le profil Boursorama."
                />
                <StatCard
                  icon={<Star className="size-4" />}
                  label="Posts aimés"
                  value={data.posts_liked_count}
                  hint="Messages d'autres membres que ce profil a aimés."
                />
                <StatCard
                  icon={<ThumbsUp className="size-4" />}
                  label="J'aime reçus"
                  value={data.likes_received_count}
                  hint="« J'aime » reçus sur ses messages."
                />
                <StatCard
                  icon={<UsersIcon className="size-4" />}
                  label="Suit"
                  value={data.following_count}
                  hint="Membres que ce profil suit."
                />
                <StatCard
                  icon={<UsersIcon className="size-4" />}
                  label="Suivi par"
                  value={data.followers_count}
                  hint="Membres qui suivent ce profil."
                />
              </>
            ) : (
              <div className="col-span-2 md:col-span-5 rounded-lg border border-dashed border-border bg-surface-muted/40 px-4 py-3 flex items-center gap-3">
                <Download className="size-5 text-foreground-muted shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">Profil complet non importé</p>
                  <p className="text-xs text-foreground-muted">
                    Importe le profil pour révéler posts créés/aimés, j'aime reçus
                    et le graphe social — via « Importer le profil complet » ci-dessus.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Posts récents observés via le scraper de threads */}
      {data.recent && data.recent.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Posts récents observés ({data.recent.length})</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {data.recent.slice(0, 10).map((p) => (
              <div key={p.id} className="border-l-2 border-border pl-3 hover:border-primary/50 transition-colors">
                <Link
                  to={`/threads/${p.thread_id}`}
                  className="block text-sm font-medium leading-snug hover:text-primary transition-colors"
                  title={p.thread_title ?? `Thread #${p.thread_id}`}
                >
                  {p.thread_title || `Thread #${p.thread_id}`}
                </Link>
                <div className="flex items-center gap-1.5 text-xs text-foreground-muted mt-0.5">
                  {p.forum_title && (
                    <>
                      <span className="truncate max-w-[14rem]">{p.forum_title}</span>
                      <span aria-hidden>·</span>
                    </>
                  )}
                  <span title={p.posted_at}>{fromNowFR(p.posted_at)}</span>
                </div>
                <p className="text-sm mt-1.5 line-clamp-3 whitespace-pre-wrap text-foreground-muted">{p.content}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* History (importé via profileimport) */}
      <Card>
        <CardHeader>
          <CardTitle>Historique complet ({history.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-foreground-muted">
              Aucun historique importé pour ce user. Clique sur « Importer le profil complet »
              en haut pour récupérer tous les posts créés/aimés + le graphe social depuis Boursorama.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Titre</TableHead>
                  <TableHead className="text-right">Réponses</TableHead>
                  <TableHead className="text-right">J'aime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.slice(0, 50).map((h: any) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-xs text-foreground-muted">{fromNowFR(h.posted_at)}</TableCell>
                    <TableCell>
                      <Badge variant={h.kind === "created" ? "default" : "secondary"}>{h.kind}</Badge>
                    </TableCell>
                    <TableCell>
                      <a href={h.thread_url} target="_blank" rel="noreferrer" className="hover:underline">
                        {h.title}
                      </a>
                      <div className="text-xs text-foreground-muted">{h.forum_slug}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(h.comments)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(h.likes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Follow graph */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>Suit ({graph?.following.length ?? 0})</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {graph?.following.map((u: any) => (
              <Link
                key={u.id}
                to={`/users/${u.id}`}
                className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-muted"
              >
                {u.username}
              </Link>
            ))}
            {(!graph?.following || graph.following.length === 0) && (
              <span className="text-sm text-foreground-muted">—</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Suivi par ({graph?.followers.length ?? 0})</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {graph?.followers.map((u: any) => (
              <Link
                key={u.id}
                to={`/users/${u.id}`}
                className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-muted"
              >
                {u.username}
              </Link>
            ))}
            {(!graph?.followers || graph.followers.length === 0) && (
              <span className="text-sm text-foreground-muted">—</span>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | null;
  hint?: string;
}) {
  const body = (
    <Card className="h-full">
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-foreground-muted text-xs">
          {icon}
          <span className="truncate">{label}</span>
        </div>
        <div className="text-2xl font-semibold tabular-nums mt-1">
          {value == null ? "—" : formatNumber(value)}
        </div>
      </CardContent>
    </Card>
  );
  if (!hint) return body;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{body}</div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[16rem]">{hint}</TooltipContent>
    </Tooltip>
  );
}
